import { Button, TooltipDialog, ToggleButton } from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";
import { ASSIGNMENT_ACTIONS } from "@/assignments/useAssignmentReducer";
import { Assignment } from "@/types/models";
import InfoIcon from "@/icons/InfoIcon";
import getAssignmentSetting from "@/view/utils/getAssignmentSetting";
import { TOOLTIP_CONTENT } from "./GeniusActivityPanel.constants";
import {
  activityPanelContainerCss,
  activityPanelHeaderCss,
  activityPanelToggleCss,
  tooltipCss,
  tooltipIconCss,
  disabledToggleCss,
} from "./GeniusActivityPanel.style";

const TooltipDialogWithTrackEvent = withTrackEvent(TooltipDialog);
const ToggleButtonWithTrackEvent = withTrackEvent(ToggleButton);

interface GeniusActivityPanelProps {
  assignment: Assignment;
  dispatch: (action: { type: string; payload: any }) => void;
}

const GeniusActivityPanel = ({
  assignment,
  dispatch,
}: GeniusActivityPanelProps) => {
  const showDiscussionAnswers = !getAssignmentSetting(
    "hide_generation_genius_discussion_answers",
    assignment,
  );
  const showVocabDefinitions = !getAssignmentSetting(
    "hide_generation_genius_vocab_definitions",
    assignment,
  );
  const isVideoOnly = getAssignmentSetting(
    "show_generation_genius_video_only",
    assignment,
  );

  const handleConfigUpdate = (configKey: string, value: boolean) => {
    dispatch({
      type: ASSIGNMENT_ACTIONS.SET_ASSIGNMENT_CONFIG,
      payload: { [configKey]: value },
    });
  };

  const handleDiscussionAnswersToggle = () => {
    if (isVideoOnly) return; // Prevent toggling when video-only mode is enabled (button is disabled)

    handleConfigUpdate(
      "hide_generation_genius_discussion_answers",
      showDiscussionAnswers,
    );
  };

  const handleVocabDefinitionsToggle = () => {
    if (isVideoOnly) return; // Prevent toggling when video-only mode is enabled (button is disabled)

    handleConfigUpdate(
      "hide_generation_genius_vocab_definitions",
      showVocabDefinitions,
    );
  };

  const handleVideoOnlyToggle = () => {
    const newVideoOnlyValue = !isVideoOnly;

    // Update all assignment settings to align with the current video only state
    handleConfigUpdate("show_generation_genius_video_only", newVideoOnlyValue);
    handleConfigUpdate(
      "hide_generation_genius_discussion_answers",
      newVideoOnlyValue,
    );
    handleConfigUpdate(
      "hide_generation_genius_vocab_definitions",
      newVideoOnlyValue,
    );
  };

  const renderTooltip = (content: string, qaSelector: string) => (
    <TooltipDialogWithTrackEvent
      __cssFor={{
        Button: { Icon: { root: tooltipIconCss } },
        dialog: tooltipCss,
      }}
      ariaProps={{ "aria-label": content }}
      body={content}
      buttonProps={{
        flavor: Button.flavors.incognito,
      }}
      // @ts-expect-error Property 'css' does not exist on type
      buttonContents={<InfoIcon css={tooltipIconCss} />}
      dataProps={{
        "data-qa-selector": qaSelector,
      }}
      dialogAriaProps={{
        "aria-label": "Activity panel setting information",
      }}
      placement="top"
      fallbackPlacements={["top-start"]}
      objectType={objectTypes.assignment}
      actionPrefix="Initiate"
      actionName={actionNames.interact}
      streamProperties={{
        description: `Open ${qaSelector} tooltip`,
      }}
    />
  );

  return (
    <div css={activityPanelContainerCss}>
      <div css={activityPanelHeaderCss}>
        <h3>Activity panel</h3>
      </div>

      <div css={[activityPanelToggleCss, isVideoOnly && disabledToggleCss]}>
        <ToggleButtonWithTrackEvent
          dataProps={{
            "data-qa-selector": "show-discussion-questions-answers-toggle",
          }}
          onClick={handleDiscussionAnswersToggle}
          pressed={showDiscussionAnswers && !isVideoOnly}
          label="Show discussion questions answers"
          objectType={objectTypes.assignment}
          actionPrefix="Initiate"
          actionName={actionNames.interact}
          streamProperties={{
            description: "Toggle discussion questions answers",
          }}
        />
        {renderTooltip(
          TOOLTIP_CONTENT.discussionQuestions,
          "discussion-questions-tooltip",
        )}
      </div>

      <div css={[activityPanelToggleCss, isVideoOnly && disabledToggleCss]}>
        <ToggleButtonWithTrackEvent
          dataProps={{
            "data-qa-selector": "show-vocabulary-definitions-toggle",
          }}
          onClick={handleVocabDefinitionsToggle}
          pressed={showVocabDefinitions && !isVideoOnly}
          label="Show vocabulary definitions"
          objectType={objectTypes.assignment}
          actionPrefix="Initiate"
          actionName={actionNames.interact}
          streamProperties={{
            description: "Toggle vocabulary definitions",
          }}
        />
        {renderTooltip(
          TOOLTIP_CONTENT.vocabularyDefinitions,
          "vocabulary-definitions-tooltip",
        )}
      </div>

      <div css={activityPanelToggleCss}>
        <ToggleButtonWithTrackEvent
          dataProps={{
            "data-qa-selector": "video-only-toggle",
          }}
          onClick={handleVideoOnlyToggle}
          pressed={isVideoOnly}
          label="Video only"
          objectType={objectTypes.assignment}
          actionPrefix="Initiate"
          actionName={actionNames.interact}
          streamProperties={{
            description: "Toggle video only",
          }}
        />
        {renderTooltip(TOOLTIP_CONTENT.videoOnly, "video-only-tooltip")}
      </div>
    </div>
  );
};

export default GeniusActivityPanel;
